/**
 * Achievements and titles
 * ([Achievements](../../docs/ACHIEVEMENTS.md)).
 *
 * Forty-two badges and twenty-four titles, for the weeks after daily novelty
 * wears off. Three things here are the design rather than the implementation
 * ([D-106](../../docs/DECISIONS.md#d-106)).
 *
 * **Everything is derived, nothing is counted.** An achievement asks a question
 * of a `Standing` — a snapshot of facts about what a child has actually done —
 * and the server builds that snapshot from the attempt log. There is no
 * `achievements_progress` table and no counter to increment, so nothing can
 * drift out of step with what happened, and **an achievement added next year
 * awards correctly for history already recorded**. It is the same rule the
 * derived ids follow ([D-087](../../docs/DECISIONS.md#d-087)): a stateful
 * question ("has this just become true?") replaced by a stateless one ("is it
 * true?").
 *
 * **There is no percentage, and nowhere to put one.** "You have 7 of 42" is a
 * true statement a child reads as 83% failure
 * ([D-031](../../docs/DECISIONS.md#d-031)). So `Shelf` carries no total, no
 * count and no ratio — not a field set to zero, **no field**. A screen cannot
 * display a number this module will not compute.
 *
 * **A locked achievement is a hint, never a failure.** Every entry carries a
 * `hint` written as an invitation — *"Finish a chapter without any mistakes"* —
 * and there is no `locked` copy anywhere saying what a child has not done.
 */
import { EXERCISE_TYPES } from './exercises.js';
import { SLOTS } from './shop.js';
/**
 * Derived from the catalogues rather than written down.
 *
 * *Tried everything* and *Dressed up* both mean "all of them", and an
 * achievement whose target is a number somebody has to remember to update is an
 * achievement that silently becomes impossible the day a type or a slot is
 * added.
 */
const EXERCISE_TYPE_COUNT = EXERCISE_TYPES.length;
const WARDROBE_SLOTS = SLOTS.length;
/** A standing where a child has done nothing at all. Their first day. */
export const NOTHING_YET = {
    lessonsDone: 0,
    chaptersDone: 0,
    booksDone: 0,
    chaptersMastered: 0,
    chapterReached: 0,
    chaptersReplayedHarder: 0,
    streakDays: 0,
    bestStreak: 0,
    longestReturn: 0,
    fullWeeks: 0,
    vacationsTaken: 0,
    earliestFinishHour: null,
    latestFinishHour: null,
    wordsKnown: 0,
    bestCombo: 0,
    perfectLessons: 0,
    perfectChapters: 0,
    chaptersWithoutHints: 0,
    speakingDone: 0,
    dictationCorrect: 0,
    soundsMastered: 0,
    bestBonusRound: 0,
    exerciseTypesTried: 0,
    storiesDone: 0,
    chestsOpened: 0,
    chestItemsWon: 0,
    wardrobeSlotsFilled: 0,
    titlesWorn: 0,
    shuffleUsed: 0,
    acornsPeak: 0,
};
export const FAMILIES = ['progress', 'persistence', 'skill', 'exploration'];
/** `at least n of this` — the shape most of the catalogue has. */
const atLeast = (of, n) => (s) => {
    const value = s[of];
    return typeof value === 'number' && value >= n;
};
export const ACHIEVEMENTS = [
    // --- progress -------------------------------------------------------------
    {
        id: 'ach.first_steps',
        nameL1: 'First Steps',
        hint: 'Finish your first lesson',
        badge: '🌱',
        family: 'progress',
        xp: 20,
        acorns: 20,
        grantsTitle: 'title.first_steps',
        reached: atLeast('lessonsDone', 1),
    },
    {
        id: 'ach.chapter_one',
        nameL1: 'Chapter One',
        hint: 'Finish your first chapter',
        badge: '📍',
        family: 'progress',
        xp: 50,
        acorns: 40,
        reached: atLeast('chaptersDone', 1),
    },
    {
        id: 'ach.book_finisher',
        nameL1: 'Book Finisher',
        hint: 'Finish a whole book',
        badge: '📗',
        family: 'progress',
        xp: 300,
        acorns: 250,
        grantsTitle: 'title.explorer',
        reached: atLeast('booksDone', 1),
    },
    {
        id: 'ach.halfway',
        nameL1: 'Halfway There',
        hint: 'Reach chapter 50',
        badge: '🗺️',
        family: 'progress',
        xp: 400,
        acorns: 300,
        reached: atLeast('chapterReached', 50),
    },
    {
        id: 'ach.long_path',
        nameL1: 'The Long Path',
        hint: 'Finish all five books',
        badge: '🏔️',
        family: 'progress',
        xp: 1000,
        acorns: 1000,
        grantsTitle: 'title.thinker',
        reached: atLeast('booksDone', 5),
    },
    {
        id: 'ach.ten_chapters',
        nameL1: 'Ten Chapters',
        hint: 'Finish 10 chapters',
        badge: '✨',
        family: 'progress',
        xp: 100,
        acorns: 80,
        reached: atLeast('chaptersDone', 10),
    },
    {
        id: 'ach.fifty_lessons',
        nameL1: 'Fifty Lessons',
        hint: 'Finish 50 lessons',
        badge: '🎯',
        family: 'progress',
        xp: 100,
        acorns: 80,
        reached: atLeast('lessonsDone', 50),
    },
    {
        id: 'ach.hundred_lessons',
        nameL1: 'Hundred Lessons',
        hint: 'Finish 100 lessons',
        badge: '💯',
        family: 'progress',
        xp: 200,
        acorns: 150,
        reached: atLeast('lessonsDone', 100),
    },
    {
        id: 'ach.five_hundred',
        nameL1: 'Five Hundred',
        hint: 'Finish 500 lessons',
        badge: '🌟',
        family: 'progress',
        xp: 600,
        acorns: 500,
        reached: atLeast('lessonsDone', 500),
    },
    {
        id: 'ach.second_time',
        nameL1: 'Second Time Round',
        hint: 'Replay a chapter at a harder level',
        badge: '🔁',
        family: 'progress',
        xp: 60,
        acorns: 50,
        reached: atLeast('chaptersReplayedHarder', 1),
    },
    {
        id: 'ach.mastered',
        nameL1: 'Mastered',
        hint: 'Take a chapter all the way to Mastered',
        badge: '●',
        family: 'progress',
        xp: 150,
        acorns: 120,
        reached: atLeast('chaptersMastered', 1),
    },
    {
        id: 'ach.five_mastered',
        nameL1: 'Five Mastered',
        hint: 'Take five chapters to Mastered',
        badge: '⬤',
        family: 'progress',
        xp: 400,
        acorns: 350,
        reached: atLeast('chaptersMastered', 5),
    },
    // --- persistence ----------------------------------------------------------
    {
        id: 'ach.three_in_a_row',
        nameL1: 'Three in a Row',
        hint: 'Play three days running',
        badge: '🔥',
        family: 'persistence',
        xp: 30,
        acorns: 25,
        reached: atLeast('bestStreak', 3),
    },
    {
        id: 'ach.one_week',
        nameL1: 'One Week',
        hint: 'Play seven days running',
        badge: '🔥',
        family: 'persistence',
        xp: 70,
        acorns: 60,
        reached: atLeast('bestStreak', 7),
    },
    {
        id: 'ach.two_weeks',
        nameL1: 'Two Weeks',
        hint: 'Play fourteen days running',
        badge: '🔥',
        family: 'persistence',
        xp: 120,
        acorns: 100,
        reached: atLeast('bestStreak', 14),
    },
    {
        id: 'ach.one_month',
        nameL1: 'One Month',
        hint: 'Play thirty days running',
        badge: '🔥',
        family: 'persistence',
        xp: 250,
        acorns: 200,
        reached: atLeast('bestStreak', 30),
    },
    {
        id: 'ach.hundred_days',
        nameL1: 'Hundred Days',
        hint: 'Play a hundred days running',
        badge: '🔥',
        family: 'persistence',
        xp: 800,
        acorns: 700,
        grantsTitle: 'title.unbroken',
        reached: atLeast('bestStreak', 100),
    },
    {
        id: 'ach.a_whole_year',
        nameL1: 'A Whole Year',
        hint: 'Play every day for a year',
        badge: '🔥',
        family: 'persistence',
        xp: 2000,
        acorns: 2000,
        grantsTitle: 'title.year_one',
        reached: atLeast('bestStreak', 365),
    },
    {
        /*
          The one worth pointing at. It rewards the exact moment most apps punish —
          a child coming back after a lapse — and it is the clearest expression of
          [Product](../../docs/PRODUCT.md#design-principles) principle 7 anywhere in
          the app.
        */
        id: 'ach.you_came_back',
        nameL1: 'You Came Back',
        hint: 'Come back after a week away — we will be glad to see you',
        badge: '👋',
        family: 'persistence',
        xp: 60,
        acorns: 50,
        grantsTitle: 'title.returner',
        reached: atLeast('longestReturn', 7),
    },
    {
        id: 'ach.early_bird',
        nameL1: 'Early Bird',
        hint: 'Finish a lesson before eight in the morning',
        badge: '🌅',
        family: 'persistence',
        xp: 40,
        acorns: 30,
        reached: (s) => s.earliestFinishHour !== null && s.earliestFinishHour < 8,
    },
    {
        id: 'ach.night_owl',
        nameL1: 'Night Owl',
        hint: 'Finish a lesson after eight in the evening',
        badge: '🌙',
        family: 'persistence',
        xp: 40,
        acorns: 30,
        reached: (s) => s.latestFinishHour !== null && s.latestFinishHour >= 20,
    },
    {
        id: 'ach.every_day_this_week',
        nameL1: 'Every Day This Week',
        hint: 'Play on all seven days of one week',
        badge: '📅',
        family: 'persistence',
        xp: 100,
        acorns: 80,
        reached: atLeast('fullWeeks', 1),
    },
    {
        id: 'ach.well_planned',
        nameL1: 'Well Planned',
        hint: 'Take a holiday with vacation mode, and come back',
        badge: '🏖️',
        family: 'persistence',
        xp: 50,
        acorns: 40,
        reached: atLeast('vacationsTaken', 1),
    },
    // --- skill ----------------------------------------------------------------
    {
        id: 'ach.fifty_words',
        nameL1: 'Fifty Words',
        hint: 'Know fifty words',
        badge: '🔤',
        family: 'skill',
        xp: 60,
        acorns: 50,
        reached: atLeast('wordsKnown', 50),
    },
    {
        id: 'ach.hundred_words',
        nameL1: 'Hundred Words',
        hint: 'Know a hundred words',
        badge: '📚',
        family: 'skill',
        xp: 120,
        acorns: 100,
        reached: atLeast('wordsKnown', 100),
    },
    {
        id: 'ach.five_hundred_words',
        nameL1: 'Five Hundred Words',
        hint: 'Know five hundred words',
        badge: '🧠',
        family: 'skill',
        xp: 500,
        acorns: 400,
        grantsTitle: 'title.keeper_of_words',
        reached: atLeast('wordsKnown', 500),
    },
    {
        id: 'ach.on_fire',
        nameL1: 'On Fire',
        hint: 'Get ten right in a row',
        badge: '🔥',
        family: 'skill',
        xp: 80,
        acorns: 60,
        reached: atLeast('bestCombo', 10),
    },
    {
        id: 'ach.unstoppable',
        nameL1: 'Unstoppable',
        hint: 'Get twenty right in a row',
        badge: '🚀',
        family: 'skill',
        xp: 200,
        acorns: 160,
        reached: atLeast('bestCombo', 20),
    },
    {
        id: 'ach.perfect_lesson',
        nameL1: 'Perfect Lesson',
        hint: 'Finish a lesson with no mistakes',
        badge: '💎',
        family: 'skill',
        xp: 50,
        acorns: 40,
        reached: atLeast('perfectLessons', 1),
    },
    {
        id: 'ach.perfect_chapter',
        nameL1: 'Perfect Chapter',
        hint: 'Finish a whole chapter with no mistakes',
        badge: '👑',
        family: 'skill',
        xp: 250,
        acorns: 200,
        grantsTitle: 'title.perfectionist',
        reached: atLeast('perfectChapters', 1),
    },
    {
        id: 'ach.no_help_needed',
        nameL1: 'No Help Needed',
        hint: 'Finish a chapter without using a hint',
        badge: '🙌',
        family: 'skill',
        xp: 150,
        acorns: 120,
        reached: atLeast('chaptersWithoutHints', 1),
    },
    {
        id: 'ach.brave_speaker',
        nameL1: 'Brave Speaker',
        hint: 'Say fifty things out loud',
        badge: '🗣️',
        family: 'skill',
        xp: 100,
        acorns: 80,
        reached: atLeast('speakingDone', 50),
    },
    {
        id: 'ach.hundred_spoken',
        nameL1: 'Hundred Spoken',
        hint: 'Say a hundred things out loud',
        badge: '🎤',
        family: 'skill',
        xp: 200,
        acorns: 160,
        reached: atLeast('speakingDone', 100),
    },
    {
        id: 'ach.tricky_sounds',
        nameL1: 'Tricky Sounds',
        hint: 'Turn a sound you found hard into one you find easy',
        badge: '🦷',
        family: 'skill',
        xp: 150,
        acorns: 120,
        reached: atLeast('soundsMastered', 1),
    },
    {
        id: 'ach.good_ears',
        nameL1: 'Good Ears',
        hint: 'Write down twenty-five things you heard, correctly',
        badge: '🎧',
        family: 'skill',
        xp: 120,
        acorns: 100,
        reached: atLeast('dictationCorrect', 25),
    },
    {
        id: 'ach.quick_thinker',
        nameL1: 'Quick Thinker',
        hint: 'Get fifteen right in one bonus round',
        badge: '⚡',
        family: 'skill',
        xp: 100,
        acorns: 80,
        reached: atLeast('bestBonusRound', 15),
    },
    // --- exploration ----------------------------------------------------------
    {
        id: 'ach.tried_everything',
        nameL1: 'Tried Everything',
        hint: 'Try every kind of question once',
        badge: '🧭',
        family: 'exploration',
        xp: 120,
        acorns: 100,
        reached: atLeast('exerciseTypesTried', EXERCISE_TYPE_COUNT),
    },
    {
        id: 'ach.story_explorer',
        nameL1: 'Story Explorer',
        hint: 'Finish your first story',
        badge: '📖',
        family: 'exploration',
        xp: 60,
        acorns: 50,
        reached: atLeast('storiesDone', 1),
    },
    {
        id: 'ach.bookworm',
        nameL1: 'Bookworm',
        hint: 'Finish twenty-five stories',
        badge: '📕',
        family: 'exploration',
        xp: 400,
        acorns: 350,
        reached: atLeast('storiesDone', 25),
    },
    {
        id: 'ach.chest_opener',
        nameL1: 'Chest Opener',
        hint: 'Open twenty-five chests',
        badge: '🎁',
        family: 'exploration',
        xp: 80,
        acorns: 0,
        reached: atLeast('chestsOpened', 25),
    },
    {
        id: 'ach.lucky',
        nameL1: 'Lucky',
        hint: 'Find something to wear inside a chest',
        badge: '💫',
        family: 'exploration',
        xp: 60,
        acorns: 0,
        reached: atLeast('chestItemsWon', 1),
    },
    {
        id: 'ach.dressed_up',
        nameL1: 'Dressed Up',
        hint: 'Wear something in every slot at once',
        badge: '👕',
        family: 'exploration',
        xp: 150,
        acorns: 120,
        grantsTitle: 'title.fully_dressed',
        reached: atLeast('wardrobeSlotsFilled', WARDROBE_SLOTS),
    },
    {
        id: 'ach.titled',
        nameL1: 'Titled',
        hint: 'Wear a title under your name',
        badge: '🏅',
        family: 'exploration',
        xp: 50,
        acorns: 40,
        reached: atLeast('titlesWorn', 1),
    },
    {
        id: 'ach.surprise_me',
        nameL1: 'Surprise Me',
        hint: 'Let the wardrobe pick an outfit for you',
        badge: '🎲',
        family: 'exploration',
        xp: 20,
        acorns: 20,
        reached: atLeast('shuffleUsed', 1),
    },
    {
        id: 'ach.saver',
        nameL1: 'Saver',
        hint: 'Save up two thousand acorns',
        badge: '🌰',
        family: 'exploration',
        xp: 100,
        acorns: 0,
        reached: atLeast('acornsPeak', 2000),
    },
];
export const achievementById = (id) => ACHIEVEMENTS.find((each) => each.id === id);
// --- titles -----------------------------------------------------------------
export const TITLE_TIERS = ['starter', 'proud', 'serious', 'legendary'];
/**
 * Twenty-four titles, worn under a child's name.
 *
 * **Every one is positive.** No joke titles at a child's expense, and nothing
 * that reads as a put-down if a classmate sees it — which is the only rule that
 * matters here, since a title is the one thing in the app another person sees.
 *
 * Earned titles cost nothing, so a child who never saves up still has plenty to
 * wear, and they arrive throughout rather than all at the end.
 */
export const TITLES = [
    {
        id: 'title.word_collector',
        textL1: 'Word Collector',
        tier: 'starter',
        priceAcorns: 500,
        earnedBy: null,
    },
    {
        id: 'title.good_listener',
        textL1: 'Good Listener',
        tier: 'starter',
        priceAcorns: 500,
        earnedBy: null,
    },
    {
        id: 'title.early_bird',
        textL1: 'Early Bird',
        tier: 'starter',
        priceAcorns: 500,
        earnedBy: null,
    },
    {
        id: 'title.curious_one',
        textL1: 'The Curious One',
        tier: 'starter',
        priceAcorns: 500,
        earnedBy: null,
    },
    {
        id: 'title.story_explorer',
        textL1: 'Story Explorer',
        tier: 'proud',
        priceAcorns: 1500,
        earnedBy: null,
    },
    {
        id: 'title.sound_master',
        textL1: 'Sound Master',
        tier: 'proud',
        priceAcorns: 1500,
        earnedBy: null,
    },
    {
        id: 'title.question_asker',
        textL1: 'Question Asker',
        tier: 'proud',
        priceAcorns: 1500,
        earnedBy: null,
    },
    {
        id: 'title.patient_one',
        textL1: 'The Patient One',
        tier: 'proud',
        priceAcorns: 1500,
        earnedBy: null,
    },
    {
        id: 'title.unstoppable',
        textL1: 'Unstoppable',
        tier: 'serious',
        priceAcorns: 4000,
        earnedBy: null,
    },
    {
        id: 'title.brave_speaker',
        textL1: 'The Brave Speaker',
        tier: 'serious',
        priceAcorns: 4000,
        earnedBy: null,
    },
    {
        id: 'title.chapter_champion',
        textL1: 'Chapter Champion',
        tier: 'serious',
        priceAcorns: 4000,
        earnedBy: null,
    },
    {
        id: 'title.keeper_of_words',
        textL1: 'Keeper of Words',
        tier: 'serious',
        priceAcorns: 4000,
        earnedBy: 'ach.five_hundred_words',
    },
    {
        id: 'title.legend_of_the_path',
        textL1: 'Legend of the Path',
        tier: 'legendary',
        priceAcorns: 10000,
        earnedBy: null,
    },
    {
        id: 'title.best_friend',
        textL1: "Sneakers' Best Friend",
        tier: 'legendary',
        priceAcorns: 10000,
        earnedBy: null,
    },
    {
        id: 'title.first_steps',
        textL1: 'First Steps',
        tier: 'starter',
        priceAcorns: null,
        earnedBy: 'ach.first_steps',
    },
    {
        id: 'title.explorer',
        textL1: 'Explorer',
        tier: 'starter',
        priceAcorns: null,
        earnedBy: 'ach.book_finisher',
    },
    {
        id: 'title.traveller',
        textL1: 'Traveller',
        tier: 'proud',
        priceAcorns: null,
        earnedBy: 'ach.halfway',
    },
    {
        id: 'title.thinker',
        textL1: 'Thinker',
        tier: 'legendary',
        priceAcorns: null,
        earnedBy: 'ach.long_path',
    },
    {
        id: 'title.unbroken',
        textL1: 'The Unbroken',
        tier: 'serious',
        priceAcorns: null,
        earnedBy: 'ach.hundred_days',
    },
    {
        id: 'title.year_one',
        textL1: 'Year One',
        tier: 'legendary',
        priceAcorns: null,
        earnedBy: 'ach.a_whole_year',
    },
    {
        id: 'title.perfectionist',
        textL1: 'Perfectionist',
        tier: 'serious',
        priceAcorns: null,
        earnedBy: 'ach.perfect_chapter',
    },
    {
        id: 'title.returner',
        textL1: 'The Returner',
        tier: 'starter',
        priceAcorns: null,
        earnedBy: 'ach.you_came_back',
    },
    {
        id: 'title.fully_dressed',
        textL1: 'Fully Dressed',
        tier: 'proud',
        priceAcorns: null,
        earnedBy: 'ach.dressed_up',
    },
    {
        id: 'title.master_of_sounds',
        textL1: 'Master of Sounds',
        tier: 'serious',
        priceAcorns: null,
        earnedBy: 'ach.tricky_sounds',
    },
];
export const titleById = (id) => TITLES.find((each) => each.id === id);
// --- what is true now -------------------------------------------------------
/**
 * Everything this child has reached, derived from the facts.
 *
 * Asked fresh every time rather than read from a counter, which is what makes
 * a retroactively added achievement award correctly for history already
 * recorded.
 */
export const reachedIn = (standing) => ACHIEVEMENTS.filter((each) => each.reached(standing));
/**
 * The ones to celebrate: reached now, and not already recorded.
 *
 * The caller passes what has been written down; the difference is the moment.
 * Recording is the caller's job and is idempotent, so a replayed lesson that
 * computes the same standing celebrates nothing the second time.
 */
export const newlyReached = (standing, already) => reachedIn(standing).filter((each) => !already.has(each.id));
/** Titles these achievements hand over, free. */
export const titlesFrom = (unlocked) => unlocked
    .map((each) => each.grantsTitle === undefined ? undefined : titleById(each.grantsTitle))
    .filter((title) => title !== undefined);
/**
 * The achievement screen, in the order it is read.
 *
 * **There is no count on this type, and that is the point**
 * ([D-031](../../docs/DECISIONS.md#d-031)). No `total`, no `earnedCount`, no
 * ratio — not a field set to zero, no field. A screen cannot render a
 * percentage that nothing computes, so the rule survives whoever builds the
 * next version of that screen without having read this comment.
 *
 * Earned first, newest at the top: a child opens it and sees what they have
 * done. The rest follow as hints at what is possible, in catalogue order so
 * they read as a path rather than a leaderboard.
 */
export function shelf(unlockedAt) {
    // Partitioned as they are built rather than filtered afterwards. A `filter`
    // does not narrow the type, so sorting the earned half would need a `?? 0`
    // fallback for a null that cannot be there — a branch no test could reach,
    // which is worse than the extra four lines.
    const earned = [];
    const ahead = [];
    for (const each of ACHIEVEMENTS) {
        const at = unlockedAt.get(each.id) ?? null;
        const entry = {
            id: each.id,
            nameL1: each.nameL1,
            hint: each.hint,
            badge: each.badge,
            family: each.family,
            earned: at !== null,
            at,
        };
        if (at === null)
            ahead.push(entry);
        else
            earned.push({ ...entry, at });
    }
    earned.sort((a, b) => b.at.getTime() - a.at.getTime());
    return [...earned, ...ahead];
}
//# sourceMappingURL=achievements.js.map